// packages/web-ui/src/AppShell/ContextPanel-modes/ChatMode.tsx
//
// W2-S3 Task 7.1 (AC #5/#6) — Chat destination context-panel body.
// W2-S8 — extended to mount `<SessionList>` when a sessions store is
// provided. The bare `<ModeChrome>` stub is preserved as the no-store
// fallback so existing W2-S3 tests (which render `<ChatMode />` with no
// props) keep their assertions.

import type { JSX } from 'react';
import type { StoreApi } from 'zustand';
import { ModeChrome } from './ModeChrome.js';
import { useContextPanelStatus } from './useContextPanelStatus.js';
import { SessionList } from '../../components/SessionList/index.js';
import type { SessionsSlice } from '../../state/sessions.js';

export interface ChatModeProps {
  /**
   * Vanilla zustand store providing the sessions slice. When omitted
   * (W2-S3 default), the mode renders the empty-state stub from UX §5.8.
   * When provided (W2-S8 wiring path), the mode renders the full
   * `<SessionList>` surface.
   */
  sessionsStore?: StoreApi<SessionsSlice>;
  /** Currently-active session id (from the navigation/router). */
  activeSessionId?: string | null;
  /** Called when the user activates a session row (or new-session create). */
  onActivateSession?: (sessionId: string) => void;
  /** Called with the deleted sessionIds so the host can close their tabs. */
  onSessionsDeleted?: (sessionIds: string[]) => void;
}

export function ChatMode({
  sessionsStore,
  activeSessionId,
  onActivateSession,
  onSessionsDeleted,
}: ChatModeProps = {}): JSX.Element {
  const status = useContextPanelStatus('chat');
  if (sessionsStore !== undefined) {
    return (
      <SessionList
        store={sessionsStore}
        activeSessionId={activeSessionId ?? null}
        onActivate={onActivateSession ?? (() => undefined)}
        onSessionsDeleted={onSessionsDeleted}
        testId="chat-mode-root"
      />
    );
  }
  return (
    <ModeChrome
      modeKey="chat"
      testId="chat-mode-root"
      title="Chat"
      ctaLabel="+ New"
      emptyStateText="No conversations yet. Start one →"
      errorText="Could not load conversations."
      status={status}
    />
  );
}
